import { toBase64Url, toHex, utf8 } from './bytes';
import {
  ARGON2_PARAMS,
  AUTH_TOKEN_BYTES,
  HkdfInfo,
  KDF_SALT,
  RID_KEY_BITS,
  SPACE_ID_BYTES,
} from './constants';
import { normalizePassphrase } from './passphrase';

/** Everything a device needs to sync a space, derived from the passphrase alone. */
export interface DerivedKeys {
  /** 32 lowercase hexadecimal characters; the space's public address on the server. */
  readonly spaceId: string;
  /** AES-256-GCM key for record ciphertext (non-extractable). */
  readonly encKey: CryptoKey;
  /** Write token sent as the bearer credential: base64url of 32 bytes. */
  readonly authToken: string;
  /** HMAC-SHA256 key that turns (type, id) into the opaque record identifier (non-extractable). */
  readonly ridKey: CryptoKey;
}

export interface Argon2Params {
  readonly memoryKiB: number;
  readonly iterations: number;
  readonly parallelism: number;
}

export interface DeriveKeysOptions {
  /** Argon2id cost override. For tests only: other values produce keys no real device can reproduce. */
  readonly argon2?: Argon2Params;
}

/** The Argon2id step: the 32-byte master key of a (normalised) passphrase. Runs in the worker in browsers. */
export async function computeMasterKey(
  normalizedPassphrase: string,
  params: Argon2Params = ARGON2_PARAMS,
): Promise<Uint8Array> {
  // Loaded on first use: the WebAssembly of Argon2 (about 20 kB compressed) is needed only when a passphrase is
  // entered, and in the worker; a device that already has its keys never fetches it.
  const { argon2id } = await import('./argon2');
  return argon2id({
    password: normalizedPassphrase,
    salt: KDF_SALT,
    memorySize: params.memoryKiB,
    iterations: params.iterations,
    parallelism: params.parallelism,
    hashLength: ARGON2_PARAMS.hashLength,
    outputType: 'binary',
  });
}

const EMPTY_SALT = new Uint8Array(0);

/** The HKDF step: split the master key into the four independent keys. Clears nothing; callers own `master`. */
export async function deriveKeysFromMaster(master: Uint8Array): Promise<DerivedKeys> {
  const subtle = globalThis.crypto.subtle;
  const hkdf = await subtle.importKey('raw', master as Uint8Array<ArrayBuffer>, 'HKDF', false, [
    'deriveBits',
    'deriveKey',
  ]);
  const params = (info: string): HkdfParams => ({
    name: 'HKDF',
    hash: 'SHA-256',
    salt: EMPTY_SALT,
    info: utf8(info),
  });
  const [spaceIdBits, authBits, encKey, ridKey] = await Promise.all([
    subtle.deriveBits(params(HkdfInfo.SpaceId), hkdf, SPACE_ID_BYTES * 8),
    subtle.deriveBits(params(HkdfInfo.Auth), hkdf, AUTH_TOKEN_BYTES * 8),
    subtle.deriveKey(params(HkdfInfo.Enc), hkdf, { name: 'AES-GCM', length: 256 }, false, [
      'encrypt',
      'decrypt',
    ]),
    subtle.deriveKey(
      params(HkdfInfo.Rid),
      hkdf,
      { name: 'HMAC', hash: 'SHA-256', length: RID_KEY_BITS },
      false,
      ['sign', 'verify'],
    ),
  ]);
  return {
    spaceId: toHex(new Uint8Array(spaceIdBits)),
    authToken: toBase64Url(new Uint8Array(authBits)),
    encKey,
    ridKey,
  };
}

/** Derive all keys on the calling thread. Prefer `deriveKeysInWorker` in the browser (Argon2 blocks for ~1 s). */
export async function deriveKeys(passphrase: string, options: DeriveKeysOptions = {}): Promise<DerivedKeys> {
  const master = await computeMasterKey(normalizePassphrase(passphrase), options.argon2);
  try {
    return await deriveKeysFromMaster(master);
  } finally {
    master.fill(0);
  }
}
